import React, { useEffect, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/South';
import KeyboardArrowUpIcon from '@mui/icons-material/North';
import { isNumber } from 'lodash';

import ShareComposerModal from '../SharingModals/MyReflexComposersPage/ShareComposerModal';
import { updateNotes, updateQualifiers } from '../../store/videos/videoSlice';
import { switchSidebar } from '../../store/sharedStore/sharedStoreSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { PaginationFooter } from '../modules/PaginationFooter';
import { SortOptions, SortType } from '../../enums/SORT';
import styles from './MyReflexComposers.module.scss';
import LinearProgress from '../modules/LinearProgress';
import useDebounce from '../../helpers/useDebounce';
import { OrderSelector } from '../modules/Selector';
import SearchInput from '../modules/SearchInput';
import { VIDEOFEED } from '../../enums/TYPES';
import {
  getComposersThunk,
  saveQueryOptions,
  updateComposerPixelPosition,
  updateComposerPlayControls,
  updateProgressPosition,
  updateSortOption,
  updateSortType,
} from '../../store/composers/composersSlice';
import {
  buildQueryString,
  getMyAmplifyComposersPageSortOptionAndType,
  getReflexComposersPagePaginationLimit,
  getSortStateForAmplifyComposers,
  parseQueryString,
  setMyAmplifyComposersPageSortOptionAndType,
  setReflexComposersPagePaginationLimit,
  useDimensions,
} from '../../helpers';
import { STATUS } from '../../enums/ENUMS';
import Composer from './Composer';
import DateSortHeader from '../modules/DateSortHeader';
import { useContentDragDrop } from '../modules/ContentDragDrop/useContentDragDrop';
import PinnedContentSection from '../modules/PinnedContentSection';

const MyReflexComposers = () => {
  const dispatch = useAppDispatch();
  const { widthD } = useDimensions();
  const { composers, status, page, limit, sort, total, sortOption, composerPlayControl } = useAppSelector((state) => state.composersStore);
  useEffect(() => {
    dispatch(switchSidebar(false));
    dispatch(updateNotes([]));
    dispatch(updateQualifiers([]));
    dispatch(updateProgressPosition(0));
    dispatch(updateComposerPlayControls({ ...composerPlayControl.composerControls, playedSeconds: 0 }));
    dispatch(updateComposerPixelPosition(0));
    const prefferedLimit = getReflexComposersPagePaginationLimit();
    if (prefferedLimit && isNumber(+prefferedLimit)) {
      const urlObject = parseQueryString(window.location.search);
      if (urlObject?.page && urlObject?.limit) {
        dispatch(saveQueryOptions({ page: +urlObject.page, limit: parseInt(urlObject?.limit, 10), sort }));
      } else {
        dispatch(saveQueryOptions({ page: 0, limit: parseInt(prefferedLimit, 10), sort }));
      }
    }
  }, []);
  const [composersList, setComposersList] = useState<VIDEOFEED[]>([]);

  const [search, setSearch] = useState('');
  const [pageNum, setPageNum] = useState(0);
  const debouncedValue = useDebounce<string>(search, 500);

  const fetchAllComposers = async (sortFromArg?: SortType, sortOpinionFromArg?: SortOptions) => {
    dispatch(
      getComposersThunk({
        page: pageNum,
        limit,
        search,
        sort: sortFromArg ? sortFromArg : sort,
        sortOption: sortOpinionFromArg ? sortOpinionFromArg : sortOption,
      }),
    );
  };

  useEffect(() => {
    const res = getMyAmplifyComposersPageSortOptionAndType();
    if (res) {
      const sortAndOption = res.split('/');
      if (sortAndOption[0] && sortAndOption[1]) {
        dispatch(updateSortType(sortAndOption[0]));
        dispatch(updateSortOption(sortAndOption[1]));
        fetchAllComposers(sortAndOption[0] as SortType, sortAndOption[1] as SortOptions);
      }
    } else if (composers) {
      fetchAllComposers();
    }
  }, [debouncedValue, pageNum, limit, sort, sortOption]);

  useEffect(() => {
    setComposersList(composers);
  }, [composers]);

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(saveQueryOptions({ page: 0, limit, sort }));
    setSearch(e.target.value);
  };

  const onRowsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const urlObject = parseQueryString(window.location.search);
    urlObject.page = '0';
    urlObject.limit = e.target.value;
    window.history.replaceState(null, '', `?${buildQueryString(urlObject)}`);
    dispatch(saveQueryOptions({ page: 0, limit: parseInt(e.target.value, 10), sort }));
    setReflexComposersPagePaginationLimit(parseInt(e.target.value, 10));
  };

  const onPaginate = (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
    const urlObject = parseQueryString(window.location.search);
    urlObject.page = `${newPage}`;
    urlObject.limit = `${limit}`;
    window.history.replaceState(null, '', `?${buildQueryString(urlObject)}`);
    setPageNum(newPage);
    dispatch(saveQueryOptions({ page: newPage, limit, sort }));
  };

  const onHeaderSort = (option: SortOptions) => {
    const sortT = sortOption === option && sort === SortType.ASC ? SortType.DESC : SortType.ASC;
    dispatch(updateSortType(sortT));
    setMyAmplifyComposersPageSortOptionAndType(sortT, option);
    dispatch(updateSortOption(option));
  };

  const sortHandler = (singleValue: SortOptions, option?: { label: string; value: string; order: SortType }) => {
    const selectedSortOption = option ? (option.value as SortOptions) : singleValue;
    if (option) {
      dispatch(updateSortType(option.order));
      // @ts-ignore
      setMyAmplifyComposersPageSortOptionAndType(option.order, option.value);
    } else if (sortOption === singleValue) {
      const sortT = sort === SortType.ASC ? SortType.DESC : SortType.ASC;
      dispatch(updateSortType(sortT));
      setMyAmplifyComposersPageSortOptionAndType(sortT, singleValue);
    } else {
      const sortState = JSON.parse(getSortStateForAmplifyComposers()!);
      let sortType = SortType.ASC;
      if (sortState && sortState[singleValue]) {
        sortType = sortState[singleValue];
      }
      dispatch(updateSortType(sortType));
      setMyAmplifyComposersPageSortOptionAndType(sortType, singleValue);
    }
    dispatch(updateSortOption(selectedSortOption));
  };

  const refreshComposers = () => {
    fetchAllComposers();
  };

  const composerEntries = composersList.map((composer, index) => ({ composer, index }));
  const pinnedComposerEntries = pageNum === 0
    ? composerEntries.filter(({ composer }) => Boolean((composer as any)?.is_pinned))
    : [];
  const regularComposerEntries = pageNum === 0
    ? composerEntries.filter(({ composer }) => !Boolean((composer as any)?.is_pinned))
    : composerEntries;

  const drag = useContentDragDrop<VIDEOFEED>({
    items: composersList,
    contextType: 'my_reflex_composers',
    contextParentId: null,
    firstPage: pageNum === 0,
    targetType: () => 'videofeed',
    refresh: () => { if (pageNum !== 0) setPageNum(0); else refreshComposers(); },
    disabled: status === STATUS.LOADING,
  });

  const renderComposerRow = ({ composer, index }: { composer: VIDEOFEED; index: number }) => (
    <div
      key={composer.id}
      className="mb-5"
      {...drag.rowProps(composer)}
    >
      {drag.indicator(composer)}
      <Composer
        composer={composer}
        feedIndex={index}
        pinContextType="my_reflex_composers"
        pinContextParentId={null}
        onPinUpdated={refreshComposers}
        dateSortOption={sortOption}
      />
    </div>
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.topBar}>
        <div className={styles.smallPath}>File Management</div>
        <div className={styles.pageTitle}> Online Composers </div>
      </div>
      <div className={styles.searchWrapper}>
        <SearchInput quickFilter onChange={onSearch} className={styles.search} />
        <div className={styles.sortToolbar}>
          <OrderSelector
            title={'Sorting by'}
            selectedValue={sortOption}
            selectedOrder={sort}
            options={[
              { label: 'A-Z ↓ ', value: SortOptions.NAME, order: SortType.ASC },
              { label: 'Z-A ↑ ', value: SortOptions.NAME, order: SortType.DESC },
              { label: 'Owner ↓', value: SortOptions.OWNER, order: SortType.DESC },
              { label: 'Owner ↑', value: SortOptions.OWNER, order: SortType.ASC },
              { label: 'Last Viewed ↓', value: SortOptions.LAST_VIEWED, order: SortType.DESC },
              { label: 'Last Viewed ↑', value: SortOptions.LAST_VIEWED, order: SortType.ASC },
              { label: 'Last Modified ↓', value: SortOptions.UPDATED_AT, order: SortType.DESC },
              { label: 'Last Modified ↑', value: SortOptions.UPDATED_AT, order: SortType.ASC },
              { label: 'Created At ↓', value: SortOptions.CREATED_AT, order: SortType.DESC },
              { label: 'Created At ↑', value: SortOptions.CREATED_AT, order: SortType.ASC },
            ]}
            onSelect={sortHandler}
          />
        </div>
      </div>
      <div className={styles.composersList}>
        <div className={styles.toolbar}>
          <div className={styles.container}>
            <div className={styles.leftSide}>
              <div className={styles.name}>
                <button type="button" onClick={() => onHeaderSort(SortOptions.NAME)} className={styles.btn}>
                  Name
                  {sortOption === SortOptions.NAME &&
                    (sort === SortType.ASC ? (
                      <>
                        <KeyboardArrowDownIcon className={styles.arrow} />
                      </>
                    ) : (
                      <>
                        <KeyboardArrowUpIcon className={styles.arrow} />
                      </>
                    ))}
                </button>
              </div>
            </div>
            <div className={styles.rightSide}>
              <div className={styles.owner} onClick={() => onHeaderSort(SortOptions.OWNER)}>
                Owner
                {sortOption === SortOptions.OWNER && sort === SortType.DESC && (
                  <>
                    <KeyboardArrowDownIcon className={styles.arrow} />
                  </>
                )}
                {sortOption === SortOptions.OWNER && sort === SortType.ASC && (
                  <>
                    <KeyboardArrowUpIcon className={styles.arrow} />
                  </>
                )}
              </div>
              <div className={styles.date}>
                <DateSortHeader
                  sortOption={sortOption}
                  sort={sort}
                  compact={widthD <= 1330}
                  onSelect={sortHandler}
                  arrowClassName={styles.arrow}
                />
              </div>
              <div className={styles.size}>Size</div>
              <div className={styles.actionBtns}>Actions</div>
            </div>
          </div>
        </div>
        <div className={styles.list}>
          {(status === STATUS.LOADING || drag.busy) && <LinearProgress />}
          {(pinnedComposerEntries.length > 0 || drag.active) && (
            <div key="pinned-content" {...drag.zoneProps(true)}>
              <PinnedContentSection count={pinnedComposerEntries.length}>
                {pinnedComposerEntries.map(renderComposerRow)}
                {drag.emptyHint(true, pinnedComposerEntries.length === 0)}
              </PinnedContentSection>
            </div>
          )}
          <div key="regular-content" {...drag.zoneProps(false)}>
            {regularComposerEntries.map(renderComposerRow)}
            {drag.emptyHint(false, regularComposerEntries.length === 0)}
          </div>

          {composersList.length === 0 && status === STATUS.IDLE && (
            <div className={styles.noFoldersContainer}>
              <div className={styles.noFoldersText}>No Composers</div>
              <div className="emptyStateHint">Send clips from your uploaded timelines to a new composer to see them here</div>
            </div>
          )}
        </div>
      </div>

      <PaginationFooter
        hideVersion
        page={pageNum}
        total={total}
        onPageChange={onPaginate}
        rowsPerPage={limit}
        onRowsPerPageChange={onRowsChange}
        pinnedCount={composersList.filter((composer: any) => composer?.is_pinned).length}
      />
      <ShareComposerModal />
    </div>
  );
};

export default MyReflexComposers;
